<template>
  <SidebarTabTemplate :title="$t('queue.jobHistory')">
    <template #alt-title>
      <div class="ml-auto flex shrink-0 items-center">
        <JobHistoryActionsMenu @clear-history="showQueueClearHistoryDialog" />
      </div>
    </template>
    <template #header>
      <div class="flex flex-col gap-2 pb-1">
        <div class="px-3 py-2">
          <JobFilterTabs
            v-model:selected-job-tab="selectedJobTab"
            :has-failed-jobs="hasFailedJobs"
          />
        </div>
        <JobFilterActions
          v-model:selected-workflow-filter="selectedWorkflowFilter"
          v-model:selected-sort-mode="selectedSortMode"
          v-model:search-query="searchQuery"
          class="px-3"
          :hide-show-assets-action="true"
          :show-search="true"
          :search-placeholder="t('sideToolbar.queueProgressOverlay.searchJobs')"
        />
      </div>
      <div
        class="flex items-center justify-between px-3 pb-1 text-xs leading-none text-text-primary"
      >
        <span class="text-text-secondary">{{ activeQueueSummary }}</span>
        <div class="flex items-center gap-2">
          <span class="text-xs text-base-foreground">
            {{ t('sideToolbar.queueProgressOverlay.clearQueueTooltip') }}
          </span>
          <Button
            variant="destructive"
            size="icon"
            :aria-label="
              t('sideToolbar.queueProgressOverlay.clearQueueTooltip')
            "
            :disabled="queuedCount === 0"
            @click="clearQueuedWorkflows"
          >
            <i class="icon-[lucide--list-x] size-4" />
          </Button>
        </div>
      </div>
    </template>
    <template #body>
      <JobAssetsList
        :displayed-job-groups="displayedJobGroups"
        @cancel-item="onCancelItem"
        @delete-item="onDeleteItem"
        @view-item="onViewItem"
        @menu="onMenuItem"
      />
      <JobContextMenu
        ref="jobContextMenuRef"
        :entries="jobMenuEntries"
        @action="onJobMenuAction"
      />
      <MediaLightbox
        v-model:active-index="galleryActiveIndex"
        :all-gallery-items="galleryItems as any"
      />
    </template>
  </SidebarTabTemplate>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import JobFilterActions from '@/components/queue/job/JobFilterActions.vue'
import JobFilterTabs from '@/components/queue/job/JobFilterTabs.vue'
import JobAssetsList from '@/components/queue/job/JobAssetsList.vue'
import JobContextMenu from '@/components/queue/job/JobContextMenu.vue'
import JobHistoryActionsMenu from '@/components/queue/JobHistoryActionsMenu.vue'
import type { MenuEntry } from '@/composables/queue/useJobMenu'
import type { JobListItem } from '@/composables/queue/useJobList'
import { useQueueClearHistoryDialog } from '@/composables/queue/useQueueClearHistoryDialog'
import { useErrorHandling } from '@/composables/useErrorHandling'
import SidebarTabTemplate from '@/components/sidebar/tabs/SidebarTabTemplate.vue'
import MediaLightbox from '@/components/sidebar/tabs/queue/MediaLightbox.vue'
import Button from '@/components/ui/button/Button.vue'
import { useCommandStore } from '@/stores/commandStore'
import { useDialogStore } from '@/stores/dialogStore'
import { useExecutionStore } from '@/stores/executionStore'
import { getMediaTypeFromFilename } from '@/utils/formatUtil'
//!nordy - useQueueStore → useNordyQueueStore, useJobList → useNordyJobList, useJobMenu → useNordyJobMenu로 교체
import { useNordyQueueStore } from '@/nordy/store/nordyQueueStore'
import type {
  NordyQueue,
  ResultNordyQueue
} from '@/nordy/store/nordyQueueStore'
import { useNordyJobList } from '@/nordy/composables/useNordyJobList'
import { useNordyJobMenu } from '@/nordy/composables/useNordyJobMenu'

const Load3dViewerContent = defineAsyncComponent(
  () => import('@/components/load3d/Load3dViewerContent.vue')
)

const { t, n } = useI18n()
const commandStore = useCommandStore()
const dialogStore = useDialogStore()
const executionStore = useExecutionStore()
const queueStore = useNordyQueueStore()
const { showQueueClearHistoryDialog } = useQueueClearHistoryDialog()
const { wrapWithErrorHandlingAsync } = useErrorHandling()
const {
  selectedJobTab,
  selectedWorkflowFilter,
  selectedSortMode,
  searchQuery,
  hasFailedJobs,
  filteredTasks,
  groupedJobItems
} = useNordyJobList()

const displayedJobGroups = computed(() => groupedJobItems.value)
const runningCount = computed(() => (queueStore.runningTask ? 1 : 0))
const queuedCount = computed(() => queueStore.pendingTasks.length)

const runningJobsLabel = computed(() =>
  t('sideToolbar.queueProgressOverlay.runningJobsLabel', {
    count: n(runningCount.value)
  })
)
const queuedJobsLabel = computed(() =>
  t('sideToolbar.queueProgressOverlay.queuedJobsLabel', {
    count: n(queuedCount.value)
  })
)
const activeQueueSummary = computed(() => {
  if (runningCount.value === 0 && queuedCount.value === 0) {
    return t('sideToolbar.queueProgressOverlay.noActiveJobs')
  }
  if (queuedCount.value === 0) {
    return runningJobsLabel.value
  }
  if (runningCount.value === 0) {
    return queuedJobsLabel.value
  }
  return t('sideToolbar.queueProgressOverlay.runningQueuedSummary', {
    running: runningJobsLabel.value,
    queued: queuedJobsLabel.value
  })
})

const clearQueuedWorkflows = wrapWithErrorHandlingAsync(async () => {
  const pendingJobIds = queueStore.pendingTasks
    .map((task) => task.jobId)
    .filter((id): id is string => typeof id === 'string' && id.length > 0)

  await commandStore.execute('Comfy.ClearPendingTasks')
  executionStore.clearInitializationByJobIds(pendingJobIds)
})

const galleryActiveIndex = ref(-1)
const galleryItems = ref<ResultNordyQueue[]>([])

const onViewItem = wrapWithErrorHandlingAsync(async (item: JobListItem) => {
  const nordyTask = item.taskRef as unknown as NordyQueue | undefined
  const previewOutput = nordyTask?.previewOutput

  if (!previewOutput) return

  //!nordy: LoaderManager가 지원하는 모든 3D 포맷에 대해 Load3D 뷰어를 띄운다.
  // 확장자 판별은 shared-frontend-utils의 `getMediaTypeFromFilename`을 통해
  // `THREE_D_EXTENSIONS`와 일관되게 수행한다 (glb/gltf/fbx/obj/stl/ply/splat/...).
  if (
    previewOutput.url &&
    getMediaTypeFromFilename(previewOutput.url) === '3D'
  ) {
    dialogStore.showDialog({
      key: 'asset-3d-viewer',
      title: item.title,
      component: Load3dViewerContent,
      props: {
        modelUrl: previewOutput.url || ''
      },
      dialogComponentProps: {
        style: 'width: 80vw; height: 80vh;',
        maximizable: true
      }
    })
    return
  }

  // Use all previewable outputs from the clicked task (matches upstream behavior).
  // Fall back to one preview per task only when the clicked task has no outputs.
  const taskOutputs = (nordyTask?.outputs ?? []).filter(
    (o) => o.supportsPreview
  )

  const items =
    taskOutputs.length > 0
      ? taskOutputs
      : filteredTasks.value
          .map((task) => task.previewOutput)
          .filter((o): o is ResultNordyQueue => !!o && o.supportsPreview)

  if (items.length === 0) return

  galleryItems.value = items
  const idx = items.findIndex((o) => o.url === previewOutput.url)
  galleryActiveIndex.value = idx >= 0 ? idx : 0
})

const onInspectAsset = (item: JobListItem) => {
  void onViewItem(item)
}

const currentMenuItem = ref<JobListItem | null>(null)
const jobContextMenuRef = ref<InstanceType<typeof JobContextMenu> | null>(null)

const { jobMenuEntries, cancelJob } = useNordyJobMenu(
  () => currentMenuItem.value,
  onInspectAsset
)

const onCancelItem = wrapWithErrorHandlingAsync(async (item: JobListItem) => {
  await cancelJob(item)
})

const onDeleteItem = wrapWithErrorHandlingAsync(async (item: JobListItem) => {
  const nordyTask = item.taskRef as unknown as NordyQueue | undefined
  if (!nordyTask) return
  queueStore.deleteTask(nordyTask.id)
})

const onMenuItem = (item: JobListItem, event: Event) => {
  currentMenuItem.value = item
  jobContextMenuRef.value?.open(event)
}

const onJobMenuAction = wrapWithErrorHandlingAsync(async (entry: MenuEntry) => {
  if (entry.kind === 'divider') return
  if (entry.onClick) await entry.onClick()
  jobContextMenuRef.value?.hide()
})

// Session-only: history is not loaded from API on mount.
// To restore, call: void queueStore.loadHistory();
</script>
